<template>
  <div>
    <v-card>
      <v-toolbar
        color="secondary"
        dark
      >
        <v-toolbar-side-icon><v-icon>storage</v-icon></v-toolbar-side-icon>
        <v-toolbar-title>
          {{$tc('app.box', 2)}}
          <span v-if="!$store.getters.isSubscriptionUser">{{
            $t('box.activatedCounts', [
              currentBoxesCount.activated_boxes_count,
              (userProfile.organization.max_boxes === null)
              ? $tc('box.unlimited')
                : userProfile.organization.max_boxes
            ])
          }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <div
          v-if="$vuetify.breakpoint.smAndDown"
          slot="extension"
          class="white--text w-100"
        >

          <v-text-field
            type="text"
            v-model="filterText"
            @keyup.enter="doFilter"
            :placeholder="$t('box.filterPlaceholder')"
            single-line
            hide-details
          ></v-text-field>
        </div>
        <v-select class="groups-select" :label="$t('group.select')" :items="groups"
        item-text="name" item-value="boxes"
        @input="onChange()" name="template" v-model="groupSelected" v-if="groups.length">
        </v-select>
        <v-text-field
          v-if="$vuetify.breakpoint.mdAndUp"
          type="text"
          v-model="filterText"
          @keyup.enter="doFilter"
          :placeholder="$t('box.filterPlaceholder')"
          single-line
          hide-details
        ></v-text-field>
        <v-btn
          @click.prevent="doFilter"
          icon
        >
          <v-icon>search</v-icon>
        </v-btn>

        <transition
          enter-active-class="animated bounceIn"
          leave-active-class="animated bounceOut"
        >
        <span
          v-if="selectedRows !== undefined && selectedRows.length !== 0"
        >
          <v-chip color="accent">
            {{ $tc('box.nBoxes', selectedRows.length) }}
          </v-chip>
          <!-- mass sync button -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                @click.prevent="showMassSyncDialog()"
                icon
              >
                <v-icon>refresh</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc('box.syncBox', selectedRows.length) }}</span>
          </v-tooltip>

          <!-- mass restart button -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                @click.prevent="massRestartModalVisible = true"
                icon
              >
                <v-icon>mdi-restart</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc('box.restartPlayer', selectedRows.length) }}</span>
          </v-tooltip>

          <!-- mass delete button -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                @click.prevent="massDeleteModalVisible = true"
                icon
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc('box.deleteBox', selectedRows.length) }}</span>
          </v-tooltip>

        </span>
        </transition>

      </v-toolbar>
      <v-card-text>
        <v-progress-linear
          v-show="loading"
          slot="progress"
          color="accent"
          indeterminate
        ></v-progress-linear>
        <vuetable
          ref="vuetable"
          :api-url="`${this.apiUrl}api/boxes`"
          data-path="data"
          pagination-path="meta"
          track-by="id"
          :http-fetch="httpFetch"
          :fields="fields"
          :css="css.table"
          :sort-order="sortOrder"
          :multi-sort="false"
          :append-params="moreParams"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:loading="onLoading"
          @vuetable:loaded="onLoaded"
        >
          <div slot="is-activated-slot" slot-scope="props">
            <v-btn
              v-if="props.rowData.activated"
              disabled
              fab
              flat
              small
            >
              <v-icon>check</v-icon>
            </v-btn>
            <v-btn
              v-else
              disabled
              fab
              flat
              small
            >
              <v-icon>close</v-icon>
            </v-btn>
          </div>
          <div slot="actions-slot" slot-scope="props">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="props.rowData.activated"
                  :disabled="props.rowData.status !== 2"
                  v-on="on"
                  @click.native="getBoxStats(props.rowData.id)"
                  flat
                  icon
                >
                  <v-icon>insert_chart</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('box.getBoxStats') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="$store.getters.hasPermission('edit_organization_boxes') &&
                    props.rowData.activated"
                  v-on="on"
                  @click.native="syncBox(props.rowData.id)"
                  flat
                  icon
                >
                  <v-icon>refresh</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc('box.syncBox', 1) }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!$store.getters.isSubscriptionUser && !props.rowData.activated"
                  v-on="on"
                  @click="activateBox(props.rowData.id)"
                  flat
                  icon
                >
                  <v-icon>mdi-check-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc('box.activateBox', 1) }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!$store.getters.isSubscriptionUser && props.rowData.activated"
                  v-on="on"
                  @click="deactivateBox(props.rowData.id)"
                  flat
                  icon
                >
                  <v-icon>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc('box.deactivateBox', 1) }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="$store.getters.hasPermission('edit_organization_boxes')"
                  v-on="on"
                  @click="restartBoxPlayer(props.rowData.id)"
                  flat
                  icon
                >
                  <v-icon>mdi-restart</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc('box.restartPlayer', 1) }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="$store.getters.hasPermission('edit_organization_boxes')"
                  v-on="on"
                  @click="showDeleteBoxDialog(props.rowData)"
                  flat
                  icon
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc('box.deleteBox', 1) }}</span>
            </v-tooltip>
          </div>
          <div slot="status-slot" slot-scope="props">
            <v-btn
              v-if="
                !props.rowData.status ||
                props.rowData.status === undefined ||
                props.rowData.status === 0"
              color="error"
              fab
              small
              dark
            ></v-btn>
            <v-btn
              v-else-if="props.rowData.status === 1"
              color="warning"
              fab
              small
              dark
            ></v-btn>
            <v-btn
              v-else-if="props.rowData.status === 2"
              color="success"
              fab
              small
              dark
            ></v-btn>
          </div>
        </vuetable>
      </v-card-text>

      <v-card-actions>
        <vuetable-pagination ref="pagination"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
        ></vuetable-pagination>
      </v-card-actions>
    </v-card>

    <!-- confirmation dialog: delete one box -->
    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_boxes')"
      v-model="deleteModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("box.deleteBox", 1) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="deleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="deleteBox()"
            color="accent"
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- confirmation dialog: mass delete boxes -->
    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_boxes')"
      v-model="massDeleteModalVisible"
      max-width="500px"
      scrollable
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("box.deleteBox", selectedRows.length) }}</h3>
        </v-card-title>
        <v-card-text>

        <v-alert
          :value="true"
          type="warning"
        >
          {{ $t('box.confirmMassDeleteWarning') }}
        </v-alert>

          {{ $tc('box.confirmMassDeleteMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massDeleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massDeleteBoxes()"
            color="primary"
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- confirmation dialog: mass sync boxes -->
    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_boxes')"
      v-model="massSyncModalVisible"
      max-width="500px"
      scrollable
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("box.syncBox", selectedRows.length) }}</h3>
        </v-card-title>
        <v-card-text>
          {{ $tc('box.confirmMassSyncMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massSyncModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massSyncBoxes()"
            color="primary"
          >{{ $t("general.sync") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- confirmation dialog: mass restart boxes -->
    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_boxes')"
      v-model="massRestartModalVisible"
      max-width="500px"
      scrollable
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("box.restartPlayer", selectedRows.length) }}</h3>
        </v-card-title>
        <v-card-text>
          {{ $tc('box.confirmMassRestartMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massRestartModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massRestartBoxes()"
            color="primary"
          >{{ $t("general.restart") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import VueTableMixin from '@/components/generic/VueTableMixin';
import VueTableMaterialDesignCheckbox from '@/components/generic/VueTableMaterialDesignCheckbox.vue';
import { REQUEST_BOX_STATS, UPDATE_ACTIVE_BOX_STATS, RESTART_BOX_PLAYER } from '@/store/actions/boxes';
import BoxNoteEditInput from './BoxNoteEditInput.vue';

export default {
  mixins: [
    VueTableMixin,
  ],
  computed: {
    userProfile() {
      return this.$store.state.user.profile;
    },
  },
  props: {
  },
  data() {
    return {
      allIdsOnAllPages: [],
      fields: [
        {
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
          name: VueTableMaterialDesignCheckbox,
        },
        {
          title: 'ID',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
          name: 'id',
          sortField: 'id',
        },
        {
          title: this.$i18n.t('general.name'),
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
          name: 'name',
          sortField: 'name',
        },
        {
          title: this.$i18n.t('general.note'),
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
          name: BoxNoteEditInput,
          // sortField: 'note',
        },
        {
          title: this.$i18n.tc('general.status', 1),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
          name: 'status-slot',
          sortField: 'status',
        },
        {
          title: this.$i18n.t('box.lastStatusUpdate'),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
          name: 'last_status_update',
          sortField: 'last_status_update',
        },
        {
          title: this.$i18n.tc('box.activated', 1),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
          name: 'is-activated-slot',
          sortField: 'activated',
        },
        {
          title: this.$i18n.tc('general.action', 2),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
          name: 'actions-slot',
        },
      ],
      css: {
        table: {
          tableClass: 'v-table v-datatable',
          ascendingIcon: 'mdi mdi-sort-ascending',
          descendingIcon: 'mdi mdi-sort-descending',
        },
      },
      sortOrder: [
        { field: 'id', sortField: 'id', direction: 'asc' },
        { field: 'status', sortField: 'status', direction: 'asc' },
        { field: 'note', sortField: 'note', direction: 'asc' },
      ],
      deleteModalVisible: false,
      massSyncModalVisible: false,
      massRestartModalVisible: false,
      massDeleteModalVisible: false,
      allBoxesSelected: false,
      currentBox: null,
      groups: [],
      groupSelected: null,
      boxesToAssign: [],
      currentBoxesCount: {
        activated_boxes_count: 0,
      },
    };
  },
  mounted() {
    this.getCurrentBoxesCount();
    this.getAllIdsOnAllPages();
    this.getAllGroups();

    this.$watch('allBoxesSelected', () => {
      if (this.allBoxesSelected) {
        this.boxesToAssign = this.boxes.map((box) => box.id);
      } else {
        this.boxesToAssign = [];
      }
    });
  },
  methods: {
    onChange() {
      const boxarray = [];

      const boxes = this.groupSelected;
      boxes.forEach((v) => {
          boxarray.push(v.id);
      });

      this.boxesToAssign = boxarray.concat(
        this.boxesToAssign.filter((item) => boxarray.indexOf(item) < 0),
      );

      this.boxesToAssign.forEach((v) => {
          this.$refs.vuetable.selectId(v);
      });
      this.selectedRows = this.boxesToAssign;
    },
    getAllGroups() {
      const vm = this;

      axios.get(`${window.api_url}api/groups`, {

      })
        .then((response) => {
          this.groups = response.data;
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    getCurrentBoxesCount() {
      this.getItems(
        `${this.apiUrl}api/boxes/activated/count`,
        {},
        'currentBoxesCount',
      );
    },
    getAllIdsOnAllPages() {
      const vm = this;

      axios.get(`${window.api_url}api/boxes`, {
        params: {
          paginate: false,
        },
      })
        .then((response) => {
          this.allIdsOnAllPages = response.data.data.map((item) => item.id);
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    activateBox(boxID) {
      const vm = this;

      axios.get(`${window.api_url}api/box/${boxID}/activate`, {})
        .then((response) => {
          vm.$toasted.show(response.data.message, { duration: 3000, singleton: true, type: 'success' });
          vm.$refs.vuetable.refresh();
          this.getCurrentBoxesCount();
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    massSyncBoxes() {
      const vm = this;

      axios.get(`${window.api_url}api/boxes/sync`, {
        params: {
          ids: vm.selectedRows,
        },
      })
        .then((response) => {
          this.$toasted.show(response.data.message, { duration: 5000, singleton: true, type: 'info' });
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });

      this.massSyncModalVisible = false;
    },
    massRestartBoxes() {
      const vm = this;

      axios.get(`${window.api_url}api/boxes/restart`, {
        params: {
          ids: vm.selectedRows,
        },
      })
        .then((response) => {
          this.$toasted.show(response.data.message, { duration: 5000, singleton: true, type: 'info' });
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });

      this.massRestartModalVisible = false;
    },
    massDeleteBoxes() {
      const vm = this;

      axios.delete(`${window.api_url}api/boxes`, {
        params: {
          ids: vm.selectedRows,
        },
      })
        .then((response) => {
          this.$toasted.show(response.data.message, { duration: 5000, singleton: true, type: 'info' });
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });

      this.massDeleteModalVisible = false;
    },
    deactivateBox(boxID) {
      const vm = this;

      axios.get(`${window.api_url}api/box/${boxID}/deactivate`, {})
        .then((response) => {
          vm.$toasted.show(response.data.message, { duration: 3000, singleton: true, type: 'success' });
          vm.$refs.vuetable.refresh();
          this.getCurrentBoxesCount();
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    getBoxStats(boxID) {
      this.$echo.private(`box-sync-${boxID}`).listen('.receive_box_stats', (payload) => {
        this.$store.dispatch(UPDATE_ACTIVE_BOX_STATS, payload.boxStats);
      });
      this.$store.dispatch(REQUEST_BOX_STATS, boxID);
    },
    restartBoxPlayer(boxID) {
      this.$store.dispatch(RESTART_BOX_PLAYER, boxID);
    },
    syncBox(boxID) {
      this.$toasted.show('Box sync started. We will notify you when it has finished', { duration: 5000, singleton: true, type: 'info' });

      axios.get(`${window.api_url}api/box/${boxID}/sync`);
    },
    showDeleteBoxDialog(box) {
      this.currentBox = box;
      this.deleteModalVisible = true;
    },
    deleteBox() {
      this.deleteModalVisible = false;
      this.deleteItem('box', this.currentBox.id);
    },
    showMassSyncDialog() {
      this.massSyncModalVisible = true;
    },
    showMassDeleteDialog() {
      this.massDeleteModalVisible = true;
    },
  },
};
</script>

<style scoped>
.v-input--is-disabled:hover {
  cursor: pointer;
}

.groups-select {
  margin-right: 15px;
  margin-top: 19px;
}
</style>
